import React, { useState, useEffect } from 'react';
import { Menu, X, Facebook, Instagram, Youtube, ArrowUpRight } from 'lucide-react';
 
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const menuItems = [
    { name: 'Home',        to: '/' },
    { name: 'About Us',   to: '/about-us' },
    { name: 'Leadership',  to: '/leadership' },
    { name: 'Events',      to: '/events' },
    { name: 'KYP',         to: '/products' },
    { name: 'Opportunity', to: '/opportunity' },
    { name: 'Statutory',   to: '/statutory' },
    // {name :'Knowledge', to: '/knowledge'}
  ];

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600&display=swap');

        .header-font-primary   { font-family: 'Cormorant Garamond', serif; }
        .header-font-secondary { font-family: 'Outfit', sans-serif; }

        .hdr-nav-link {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: 18px;
          letter-spacing: 0.03em;
          color: rgba(255,255,255,0.88);
          text-decoration: none;
          white-space: nowrap;
          transition: color 0.2s ease;
          line-height: 1;
        }
        .hdr-nav-link:hover { color: #ffffff; }

        @media (min-width: 1280px) {
          .hdr-nav-link { font-size: 20px; }
        }

        .hdr-nav-link-sm {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: 17px;
          letter-spacing: 0.03em;
          color: rgba(255,255,255,0.88);
          text-decoration: none;
          white-space: nowrap;
          transition: color 0.2s ease;
        }
        .hdr-nav-link-sm:hover { color: #ffffff; }

        .hdr-drawer-link {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: 22px;
          letter-spacing: 0.03em;
          color: rgba(255,255,255,0.88);
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-radius: 8px;
          transition: color 0.2s ease, background 0.2s ease;
        }
        .hdr-drawer-link:hover { color: #ffffff; background: rgba(255,255,255,0.05); }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .drawer-enter { animation: slideDown 0.22s ease forwards; }
      `}} />

      <header
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
          isScrolled
            ? 'bg-[#0f172a]/95 backdrop-blur-md shadow-lg py-2'
            : 'bg-[#0f172a]/95 lg:bg-transparent backdrop-blur-md lg:backdrop-blur-none py-2 lg:py-4 xl:py-5'
        }`}
      >
        <div className="w-full px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-16">

          {/* ═══════════════════════════════════════
              LG + XL (≥1024px) — LAPTOP & DESKTOP
          ═══════════════════════════════════════ */}
          <div
            className="hidden lg:grid max-w-screen-2xl mx-auto"
            style={{ gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '12px' }}
          >
            {/* Left — Logo */}
            <a href="/" className="flex items-center gap-2">
              <img 
                src="/assets/logo.png"
                alt="VibrantViva Logo"
                className="h-8 xl:h-10 2xl:h-12 w-auto object-contain"
              />
            </a>

            {/* Center — Nav pill */}
            <nav className="flex items-center gap-4 xl:gap-6 2xl:gap-9 bg-white/15 backdrop-blur-md rounded-full px-4 xl:px-5 2xl:px-10 py-2.5 xl:py-3.5 border border-white/20 shadow-lg">
              {menuItems.map((item) => (
              <a
                  key={item.name}
                  href={item.to}
                  className="hdr-nav-link"
                  {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {item.name}
                </a>
              ))}
            </nav>

            {/* Right — Socials + CTA */}
            <div className="flex items-center justify-end gap-2 xl:gap-4">
              <div className="flex items-center gap-2 xl:gap-3">
                {[
                  { href: 'https://www.facebook.com/media/set/?vanity=officialVibrantViva&set=a.705168821617957', icon: <Facebook className="w-3.5 h-3.5 xl:w-4 xl:h-4" /> },
                  { href: 'https://www.youtube.com/@VibrantViva',                                                  icon: <Youtube  className="w-3.5 h-3.5 xl:w-4 xl:h-4" /> },
                  { href: 'https://www.instagram.com/vibrantvivaofficial/',                                        icon: <Instagram className="w-3.5 h-3.5 xl:w-4 xl:h-4" /> },
                ].map((s, i) => (
                  <a key={i} href={s.href} target="_blank" rel="noopener noreferrer"
                    className="w-8 h-8 xl:w-10 xl:h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-all duration-300">
                    {s.icon}
                  </a>
                ))}
              </div>

              {/* Virtual Office CTA */}
              <a
                href="https://in.indusviva.com/signin"
                target="_blank" rel="noopener noreferrer"
                className="group px-5 xl:px-7 2xl:px-8 py-2.5 xl:py-3 bg-blue-500 text-white hover:bg-blue-600 font-semibold rounded-full transition-all duration-300 flex items-center gap-1.5 xl:gap-2 shadow-lg hover:shadow-blue-500/20 text-[13px] xl:text-[14px] 2xl:text-[15px] whitespace-nowrap header-font-secondary"
              >
                Virtual Office
                <ArrowUpRight className="w-3.5 h-3.5 xl:w-4 xl:h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </div>

          {/* ═══════════════════════════════════════
              MD 768–1023px — TABLET
          ═══════════════════════════════════════ */}
          <div className="hidden md:flex lg:hidden items-center justify-between gap-3 max-w-screen-xl mx-auto">
            <a href="/" className="flex items-center gap-2 flex-shrink-0 z-50">
              <img src="/assets/logo.png" alt="VibrantViva Logo" className="h-8 w-auto object-contain" />
            </a>

            <nav className="flex items-center gap-4 bg-white/15 backdrop-blur-md rounded-full px-5 py-2.5 border border-white/20 shadow-lg flex-1 mx-4 overflow-x-auto">
              {menuItems.map((item) => (
              <a
                  key={item.name}
                  href={item.to}
                  className="hdr-nav-link-sm"
                  {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {item.name}
                </a>
              ))}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex-shrink-0 z-50 text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* ═══════════════════════════════════════
              SM < 768px — MOBILE
          ═══════════════════════════════════════ */}
          <div className="flex md:hidden items-center justify-between max-w-screen-xl mx-auto">
            <a href="/" className="flex items-center gap-2 flex-shrink-0 z-50">
              <img src="/assets/logo.png" alt="VibrantViva Logo" className="h-8 w-auto object-contain" />
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="z-50 text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>
      </header>

      {/* ═══════════════════════════════════════
          DRAWER — tablet + mobile only
      ═══════════════════════════════════════ */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden drawer-enter fixed left-0 right-0 bg-[#0f172a] z-[9998] overflow-y-auto"
          style={{ top: '60px', bottom: 0 }}
        >
          <div className="px-4 md:px-10 py-6 max-w-screen-md mx-auto">

            {/* Nav links — mobile only */}
            <nav className="md:hidden space-y-1 mb-6">
              {menuItems.map((item) => (
                <a
                  key={item.name}
                  href={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="hdr-drawer-link"
                  {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {item.name}
                  <ArrowUpRight className="w-4 h-4 opacity-40" style={{ flexShrink: 0 }} />
                </a>
              ))}
            </nav>
            <div className="border-t border-white/10 mb-6" />

            {/* Socials */}
            <div className="flex justify-center md:justify-start items-center gap-4 mb-6">
              <span className="text-white/40 text-sm header-font-secondary">Follow us</span>
              {[
                { href: 'https://www.facebook.com/media/set/?vanity=officialVibrantViva&set=a.705168821617957', icon: <Facebook className="w-5 h-5" /> },
                { href: 'https://www.youtube.com/@VibrantViva',                                                  icon: <Youtube  className="w-5 h-5" /> },
                { href: 'https://www.instagram.com/vibrantvivaofficial/',                                        icon: <Instagram className="w-5 h-5" /> },
              ].map((s, i) => (
                <a key={i} href={s.href} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-all duration-300">
                  {s.icon}
                </a>
              ))}
            </div>

            {/* CTA */}
            <div className="flex justify-center md:justify-start">
              <a
                href="https://in.indusviva.com/signin"
  target="_blank" rel="noopener noreferrer"
  className="group px-5 xl:px-7 2xl:px-8 py-2.5 xl:py-3 bg-blue-500 text-white hover:bg-blue-600 font-semibold rounded-full transition-all duration-300 flex items-center gap-1.5 xl:gap-2 shadow-lg hover:shadow-blue-500/20 text-[13px] xl:text-[14px] 2xl:text-[15px] whitespace-nowrap header-font-secondary"
>
  Virtual Office
  <ArrowUpRight className="w-3.5 h-3.5 xl:w-4 xl:h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
</a>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default Header;